/**
 * Group Form — stamp PDP city/venue/product onto group-activity (no customer ask).
 * Guarded against re-entry: never dispatch → listen → DOM → observer loops.
 */
(function () {
  'use strict';

  var CONTEXT_KEYS = [
    'venue_id',
    'venue_name',
    'city',
    'state',
    'country',
    'workshop_type',
    'available_event_dates',
    'product_id',
    'product_name',
    'product_handle',
  ];

  var HIDE_VISIBLE_NAMES = [
    'venue_id',
    'venue_name',
    'state',
    'country',
    'workshop_type',
    'available_event_dates',
    'product_id',
    'product_name',
    'product_handle',
    'venue',
  ];

  /* Show city ready-filled. Schema may use city, city_2, city_name, city_state. */
  var CITY_FIELD_NAMES = ['city', 'city_2', 'city_name', 'city_state'];

  var applying = false;
  var lastCtxKey = '';

  function root() {
    return document.querySelector('[data-amc-group-form]');
  }

  function groupBlock(section) {
    section = section || root();
    if (!section) return null;
    return section.querySelector('.aogen-forms-block');
  }

  function markBlock(block) {
    if (!block) return;
    block.setAttribute('data-amc-group-form-block', '1');
    block.setAttribute('data-wbw-aogen-skip', '1');
  }

  function setHiddenInput(form, name, value) {
    if (!form || !name) return;
    var val = value == null ? '' : String(value);
    var input =
      form.querySelector('input[type="hidden"][name="' + name + '"]') ||
      form.querySelector('input[name="' + name + '"]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      form.appendChild(input);
    } else if (input.type !== 'hidden' && CITY_FIELD_NAMES.indexOf(name) === -1) {
      input.type = 'hidden';
    }
    if (input.type === 'hidden' && input.value !== val) input.value = val;
  }

  function hideStampedVisibleFields(block) {
    if (!block) return;
    HIDE_VISIBLE_NAMES.forEach(function (name) {
      var nodes = block.querySelectorAll('[name="' + name + '"]');
      for (var i = 0; i < nodes.length; i++) {
        var el = nodes[i];
        if (!el || el.type === 'hidden') continue;
        var wrap = el.closest('label') || el.closest('.aogen-forms-field') || el.parentElement;
        if (wrap && wrap !== block) {
          if (!wrap.hasAttribute('hidden')) wrap.setAttribute('hidden', '');
          if (wrap.style.display !== 'none') wrap.style.display = 'none';
          wrap.setAttribute('aria-hidden', 'true');
        } else {
          el.setAttribute('hidden', '');
          el.style.display = 'none';
        }
      }
    });
  }

  function titleCaseWords(s) {
    return String(s || '')
      .replace(/[-_]+/g, ' ')
      .replace(/\s+/g, ' ')
      .trim()
      .replace(/\b([a-z])/g, function (m, c) {
        return c.toUpperCase();
      });
  }

  function deriveCityFromTitle(title) {
    var m = String(title || '').match(/\bin\s+(.+?)\s*$/i);
    return m ? String(m[1]).trim() : '';
  }

  function deriveCityFromHandle(handle) {
    var m = String(handle || '')
      .toLowerCase()
      .match(/-in-([a-z0-9-]+)$/);
    if (!m) return '';
    return titleCaseWords(m[1]);
  }

  function fillCityFields(block, detail) {
    if (!block) return;
    var city = String((detail && detail.city) || '').trim();
    var state = String((detail && detail.state) || '').trim();
    if (!city) return;
    var cityState = state ? city + ', ' + state : city;

    function paint(el, val) {
      if (!el) return;
      if (el.value !== val) el.value = val;
      if (el.type === 'hidden') return;
      el.readOnly = true;
      el.setAttribute('readonly', '');
      el.setAttribute('aria-readonly', 'true');
      var wrap = el.closest('label') || el.closest('.aogen-forms-field') || el.parentElement;
      if (wrap && wrap !== block) {
        wrap.removeAttribute('hidden');
        wrap.style.display = '';
        wrap.removeAttribute('aria-hidden');
      }
      el.removeAttribute('hidden');
      el.style.display = '';
    }

    CITY_FIELD_NAMES.forEach(function (name) {
      var nodes = block.querySelectorAll('[name="' + name + '"]');
      for (var i = 0; i < nodes.length; i++) {
        paint(nodes[i], name === 'city_state' ? cityState : city);
      }
    });

    /* Catch schema keys like city_3 / labelled City. */
    var extras = block.querySelectorAll('input[type="text"], input[type="search"], input:not([type])');
    for (var j = 0; j < extras.length; j++) {
      var el = extras[j];
      var n = String(el.name || '');
      if (/^city(_\d+)?$/i.test(n)) {
        paint(el, city);
        continue;
      }
      var lab = el.closest('label');
      var labText = lab ? String(lab.textContent || '').replace(/\s+/g, ' ').trim() : '';
      if (/^city$/i.test(labText) || /^city\b/i.test(labText)) paint(el, city);
    }

    /* Keep canonical hidden city in sync for submit. */
    var form = block.querySelector('form') || block.querySelector('.aogen-forms-form') || block;
    setHiddenInput(form, 'city', city);
    if (state) setHiddenInput(form, 'state', state);
  }

  function ctxKey(detail) {
    detail = detail || {};
    return CONTEXT_KEYS.map(function (k) {
      return String(detail[k] || '');
    }).join('|');
  }

  function applyContext(detail) {
    detail = detail || {};
    var section = root();
    var block = groupBlock(section);
    if (!block) return false;

    var key = ctxKey(detail);
    if (key && key === lastCtxKey && block.getAttribute('data-amc-group-stamped') === '1') {
      fillCityFields(block, detail);
      hideStampedVisibleFields(block);
      return false;
    }

    if (applying) return false;
    applying = true;
    try {
      markBlock(block);
      CONTEXT_KEYS.forEach(function (k) {
        if (detail[k] == null || detail[k] === '') return;
        var attrName = 'data-' + k.replace(/_/g, '-');
        var next = String(detail[k]);
        if (block.getAttribute(attrName) !== next) block.setAttribute(attrName, next);
      });

      var form =
        block.querySelector('form') ||
        block.querySelector('.aogen-forms-form') ||
        block;
      CONTEXT_KEYS.forEach(function (k) {
        if (detail[k] == null || detail[k] === '') return;
        setHiddenInput(form, k, detail[k]);
      });
      setHiddenInput(form, 'form_key', 'group-activity');
      fillCityFields(block, detail);
      hideStampedVisibleFields(block);
      block.setAttribute('data-amc-group-stamped', '1');
      lastCtxKey = key;
    } finally {
      applying = false;
    }
    return true;
  }

  function attr(el, name) {
    if (!el || !el.getAttribute) return '';
    return String(el.getAttribute(name) || '').trim();
  }

  function firstNonEmpty() {
    for (var i = 0; i < arguments.length; i++) {
      var v = arguments[i];
      if (v != null && String(v).trim() !== '') return String(v).trim();
    }
    return '';
  }

  function wizardRoot() {
    return (
      document.querySelector('[data-popup-city-wizard]') ||
      document.querySelector('[data-amc-shop-wizard]') ||
      null
    );
  }

  function contextFromPage() {
    var section = root();
    var wiz = wizardRoot();
    var productName = firstNonEmpty(
      attr(section, 'data-product-name'),
      attr(wiz, 'data-product-title')
    );
    var productHandle = firstNonEmpty(
      attr(section, 'data-product-handle'),
      attr(wiz, 'data-product-handle')
    );
    var city = firstNonEmpty(
      attr(section, 'data-city'),
      attr(wiz, 'data-city-name'),
      deriveCityFromTitle(productName),
      deriveCityFromHandle(productHandle)
    );
    var state = firstNonEmpty(attr(section, 'data-state'), attr(wiz, 'data-state-name'));
    var country = firstNonEmpty(attr(section, 'data-country'), attr(wiz, 'data-address-country'), 'US');
    var venueId = firstNonEmpty(
      attr(section, 'data-venue-id'),
      attr(wiz, 'data-venue-ref-id'),
      attr(wiz, 'data-venue-id')
    );
    var venueName = firstNonEmpty(attr(section, 'data-venue-name'), attr(wiz, 'data-venue-name'));
    var workshopType = firstNonEmpty(
      attr(section, 'data-workshop-type'),
      attr(wiz, 'data-workshop-type')
    );
    var productId = firstNonEmpty(attr(section, 'data-product-id'), attr(wiz, 'data-product-id'));

    if (!city && !venueId && !productId) return null;

    return {
      form_key: 'group-activity',
      intent: 'inline',
      venue_id: venueId,
      venue_name: venueName,
      city: city,
      state: state,
      country: country || 'US',
      workshop_type: workshopType,
      product_id: productId,
      product_name: productName,
      product_handle: productHandle,
      available_event_dates: '',
    };
  }

  /** Notify Connect once per context key — never from our own booking-context handler. */
  function notifyConnectOnce(detail) {
    if (!detail) return;
    var key = ctxKey(detail);
    var section = root();
    if (section && section.getAttribute('data-amc-group-notified') === key) return;
    if (section) section.setAttribute('data-amc-group-notified', key);
    try {
      document.dispatchEvent(
        new CustomEvent('aogen:booking-context', {
          detail: Object.assign({}, detail, { form_key: 'group-activity', __amcGroupStamp: true }),
        })
      );
    } catch (_) {}
  }

  function stampFromPage() {
    var ctx = contextFromPage();
    if (!ctx) return false;
    var changed = applyContext(ctx);
    if (changed) notifyConnectOnce(ctx);
    else hideStampedVisibleFields(groupBlock());
    return true;
  }

  function onBookingContext(e) {
    var detail = (e && e.detail) || {};
    /* Skip our own notify to avoid stamp → event → stamp loops. */
    if (detail.__amcGroupStamp) return;
    if (detail.form_key && detail.form_key !== 'group-activity' && detail.form_key !== 'waitlist') {
      return;
    }
    var copy = {};
    CONTEXT_KEYS.forEach(function (key) {
      if (detail[key] != null && detail[key] !== '') copy[key] = detail[key];
    });
    var page = contextFromPage() || {};
    CONTEXT_KEYS.forEach(function (key) {
      if (!copy[key] && page[key]) copy[key] = page[key];
    });
    copy.form_key = 'group-activity';
    applyContext(copy);
  }

  function onVenueSelected(e) {
    var d = (e && e.detail) || {};
    var venue = d.venue || d.selectedVenue || d;
    if (!venue || typeof venue !== 'object') return;
    var page = contextFromPage() || {};
    var ctx = {
      form_key: 'group-activity',
      intent: 'inline',
      venue_id: String(venue.venue_ref_id || venue.venue_id || page.venue_id || ''),
      venue_name: String(venue.venue_name || venue.name || page.venue_name || ''),
      city: String(venue.city || venue.city_name || page.city || ''),
      state: String(venue.state || venue.state_code || page.state || ''),
      country: String(d.country || page.country || 'US'),
      workshop_type: String(d.workshop_type || page.workshop_type || ''),
      product_id: page.product_id || '',
      product_name: page.product_name || '',
      product_handle: page.product_handle || '',
      available_event_dates: '',
    };
    if (applyContext(ctx)) notifyConnectOnce(ctx);
  }

  function watchFormReady(block) {
    if (!block || block.__amcGroupReadyMo) return;
    function afterReady() {
      stampFromPage();
      /* Connect may paint fields a tick later — refill city once. */
      window.setTimeout(function () {
        var ctx = contextFromPage();
        if (ctx) fillCityFields(block, ctx);
      }, 250);
    }
    block.__amcGroupReadyMo = new MutationObserver(function (records) {
      if (applying) return;
      var readyFlip = false;
      for (var i = 0; i < records.length; i++) {
        var r = records[i];
        if (r.type === 'attributes' && r.attributeName === 'data-aogen-ready') {
          readyFlip = true;
          break;
        }
      }
      if (!readyFlip) return;
      if (block.getAttribute('data-aogen-ready') !== '1') return;
      afterReady();
    });
    block.__amcGroupReadyMo.observe(block, {
      attributes: true,
      attributeFilter: ['data-aogen-ready'],
    });
    if (block.getAttribute('data-aogen-ready') === '1') afterReady();
  }

  function boot() {
    lastCtxKey = '';
    var section = root();
    if (section) section.removeAttribute('data-amc-group-notified');
    var block = groupBlock();
    markBlock(block);
    stampFromPage();
    if (block) watchFormReady(block);
  }

  document.addEventListener('aogen:booking-context', onBookingContext);
  document.addEventListener('wbw:venue-selected', onVenueSelected);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
  document.addEventListener('shopify:section:load', function (e) {
    if (e && e.target && e.target.querySelector && e.target.querySelector('[data-amc-group-form]')) {
      boot();
    }
  });
})();
